import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../core/contexts/AuthContext';
import { api, setAccessToken } from '../core/services/api.js';
import Spinner from '../shared/components/ui/Spinner';
import Alert from '../shared/components/ui/Alert';
import { hasCompletedSetup, getUserDisplayName, getDefaultRedirectPath } from '../core/utils/authHelpers';

export default function SetupPage() {
  const { t } = useTranslation(['auth', 'common']);
  const { user, userRoles, loading: authLoading, refreshRoles, rehydrate } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [hasExistingAdmin, setHasExistingAdmin] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processing, setProcessing] = useState(false);

  // Bootstrap form (used when no user is logged in and no admin exists yet)
  const [bootstrapForm, setBootstrapForm] = useState({ email: '', password: '', fullName: '' });

  // Wait for auth to finish loading before checking status
  useEffect(() => {
    if (!authLoading) {
      checkSetupStatus();
    }
  }, [authLoading, user, userRoles]);

  const checkSetupStatus = async (attempt = 0) => {
    try {
      const res = await fetch('/api/auth/has-system-admin');
      if (res.status === 503 && attempt < 10) {
        // Server is still running migrations — retry after a short delay
        setTimeout(() => checkSetupStatus(attempt + 1), 2000);
        return;
      }
      const adminData = await res.json();
      setHasExistingAdmin(adminData.has_admin === true);

      // If no user is logged in, stop here — bootstrap or login prompt will be shown
      if (!user) {
        setLoading(false);
        return;
      }

      const invitations = await api.get('/api/auth/my-invitations');
      const enrichedInvitations = (invitations || []).map(inv => ({
        ...inv,
        id: inv.invitation_id,
        club: inv.club_id ? { id: inv.club_id, name: inv.club_name } : null,
        team: inv.team_id ? { id: inv.team_id, name: inv.team_name, club: { name: inv.club_name } } : null,
      }));
      setPendingInvitations(enrichedInvitations);

      if (hasCompletedSetup(userRoles) && enrichedInvitations.length === 0) {
        navigate(getDefaultRedirectPath(userRoles));
        return;
      }
    } catch (err) {
      console.error('Error checking setup status:', err);
      setError('Failed to load setup information: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Bootstrap: create the first admin without prior login ──────────────────
  const handleBootstrap = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      const data = await api.post('/api/auth/bootstrap', bootstrapForm);
      setAccessToken(data.access_token);
      await rehydrate();
      setSuccess(t('setup.success.adminCreated'));
      setTimeout(() => { window.location.href = '/admin'; }, 1500);
    } catch (err) {
      console.error('Bootstrap error:', err);
      setError(err.message || t('setup.error.createAdminFailed'));
      setProcessing(false);
    }
  };

  // ── Create self as system admin (already logged in) ────────────────────────
  const handleCreateSystemAdmin = async () => {
    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/api/user-roles', {
        user_id: user.id,
        role: 'system_admin',
        is_active: true,
      });

      setSuccess(t('setup.success.adminCreated'));
      await refreshRoles();
      setTimeout(() => { window.location.href = '/admin'; }, 1500);
    } catch (err) {
      console.error('Error creating system admin:', err);
      setError(t('setup.error.createAdminFailed') + err.message);
      setProcessing(false);
    }
  };

  const handleAcceptInvitation = async (invitation) => {
    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/api/auth/accept-invitation', { invitation_id: invitation.id });

      setSuccess(t('setup.success.invitationAccepted'));
      await refreshRoles();
      setTimeout(() => { window.location.href = '/'; }, 1500);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(t('setup.error.acceptInvitationFailed') + err.message);
      setProcessing(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner text={t('setup.text.loading')} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            {t('setup.heading.title')}
          </h1>
          {user && (
            <p className="text-gray-600">
              {t('setup.text.welcome')} {getUserDisplayName(user)}!
            </p>
          )}
        </div>

        {error && <Alert variant="error" className="mb-6">{error}</Alert>}
        {success && <Alert variant="success" className="mb-6">{success}</Alert>}

        {/* ── Bootstrap: no user logged in, no admin exists ── */}
        {!user && !hasExistingAdmin && (
          <div className="bg-white shadow-lg rounded-lg p-8">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-indigo-100 mb-4">
                <svg className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                {t('setup.heading.createAdmin')}
              </h2>
              <p className="text-gray-600 mb-4">{t('setup.text.firstUser')}</p>
            </div>

            <form onSubmit={handleBootstrap} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('setup.input.label.fullName')}
                </label>
                <input
                  type="text"
                  value={bootstrapForm.fullName}
                  onChange={(e) => setBootstrapForm({ ...bootstrapForm, fullName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={t('setup.input.placeholder.fullName')}
                  disabled={processing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('setup.input.label.email')}
                </label>
                <input
                  type="email"
                  required
                  value={bootstrapForm.email}
                  onChange={(e) => setBootstrapForm({ ...bootstrapForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={t('setup.input.placeholder.email')}
                  disabled={processing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('setup.input.label.password')}
                </label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={bootstrapForm.password}
                  onChange={(e) => setBootstrapForm({ ...bootstrapForm, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={t('setup.input.placeholder.password')}
                  disabled={processing}
                />
              </div>
              <button
                type="submit"
                disabled={processing}
                className="w-full px-6 py-3 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {processing ? t('setup.button.creatingAccount') : t('setup.button.createAdmin')}
              </button>
            </form>
          </div>
        )}

        {/* ── No user, admin already exists: prompt to log in ── */}
        {!user && hasExistingAdmin && (
          <div className="bg-white shadow-lg rounded-lg p-8 text-center">
            <p className="text-gray-600 mb-6">{t('setup.text.pleaseLogin')}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              {t('setup.button.goToLogin')}
            </button>
          </div>
        )}

        {/* ── Logged in: pending invitations ── */}
        {user && pendingInvitations.length > 0 && (
          <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              {t('setup.heading.pendingInvitations')}
            </h2>
            <p className="text-gray-600 mb-4">{t('setup.text.invitedToJoin')}</p>

            <div className="space-y-4">
              {pendingInvitations.map((invitation) => (
                <div key={invitation.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">
                        {invitation.club?.name || invitation.team?.club?.name || t('setup.text.unknownClub')}
                        {invitation.team && ` - ${invitation.team.name}`}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('setup.label.role')} <span className="font-medium">{formatRole(invitation.role, t)}</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        {t('setup.label.expires')} {new Date(invitation.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => handleAcceptInvitation(invitation)}
                      disabled={processing}
                      className="ml-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {processing ? t('setup.button.accepting') : t('setup.button.accept')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Logged in: create self as first admin ── */}
        {user && !hasExistingAdmin && pendingInvitations.length === 0 && (
          <div className="bg-white shadow-lg rounded-lg p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-indigo-100 mb-4">
                <svg className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                {t('setup.heading.createAdmin')}
              </h2>
              <p className="text-gray-600 mb-6">{t('setup.text.firstUser')}</p>
              <Alert variant="info" className="mb-6 text-left">
                <h3 className="font-medium text-blue-900 mb-2">{t('setup.heading.adminCapabilities')}</h3>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>{t('setup.text.capability1')}</li>
                  <li>{t('setup.text.capability2')}</li>
                  <li>{t('setup.text.capability3')}</li>
                  <li>{t('setup.text.capability4')}</li>
                </ul>
              </Alert>
              <button
                onClick={handleCreateSystemAdmin}
                disabled={processing}
                className="w-full px-6 py-3 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {processing ? t('setup.button.creatingAccount') : t('setup.button.createAdmin')}
              </button>
            </div>
          </div>
        )}

        {/* ── Logged in: admin exists but no invitations for this user ── */}
        {user && hasExistingAdmin && pendingInvitations.length === 0 && (
          <div className="bg-white shadow-lg rounded-lg p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-4">
              <svg className="h-8 w-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">{t('setup.heading.noAccess')}</h2>
            <p className="text-gray-600 mb-6">{t('setup.text.noInvitations')}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              {t('setup.button.backToLogin')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function formatRole(role, t) {
  const roleMap = {
    system_admin: t('common:roles.systemAdmin'),
    club_admin: t('common:roles.clubAdmin'),
    team_captain: t('common:roles.teamCaptain'),
    player: t('common:roles.player'),
  };
  return roleMap[role] || role;
}
